import { ForwardedRef, forwardRef, useCallback, useEffect, useState } from 'react';
import { IconCross, IconSearch } from '@components/icons';
import classNames from 'classnames';
import debounce from 'lodash.debounce';

import {
  DEBOUNCE_VARIANT_DARK,
  DEBOUNCE_VARIANT_LIGHT,
  DebouncedInputSearchProps,
} from './debounced-input-search.type';

export const DebouncedInputSearch = forwardRef(
  (
    {
      disabled,
      readOnly,
      hasError,
      placeholder,
      placeholderColor,
      defaultValue,
      className,
      canDeleteSearch,
      debounceTime = 500,
      paddingClasses = 'p-3',
      minCharacters = 2,
      variant = DEBOUNCE_VARIANT_LIGHT,
      compact = false,
      onChange,
    }: DebouncedInputSearchProps,
    ref: ForwardedRef<HTMLInputElement>
  ) => {
    const [inputValue, setInputValue] = useState(defaultValue ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const handleDebouncedValue = useCallback(
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      debounce((value: string) => onChange(value), debounceTime),
      [debounce]
    );

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        setInputValue(value);
        if (value.length >= minCharacters || value.length === 0) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call
          handleDebouncedValue(value);
        }
      },
      [handleDebouncedValue, minCharacters]
    );

    const handleClear = () => {
      setInputValue('');
      onChange('');
    };

    useEffect(() => {
      setInputValue(defaultValue ?? '');
    }, [defaultValue]);
    return (
      <div className={classNames('relative', className as string)}>
        {/* Add the SVG icon */}
        <div
          className={classNames(
            'absolute top-2.5 left-0 flex items-center pointer-events-none',
            compact ? 'pl-1' : 'pl-3'
          )}>
          <IconSearch className='h-5 w-5 text-secondary' aria-hidden={true} />
        </div>

        <input
          disabled={disabled}
          readOnly={readOnly}
          ref={ref}
          placeholder={placeholder}
          value={inputValue}
          className={classNames(
            compact
              ? 'text-body-xs text-secondary pl-7 w-full rounded-md border'
              : 'text-body-s md:text-body-m text-secondary pl-10 w-full rounded-md border',
            'placeholder-bg-secondary',
            'focus:outline-hidden focus:ring-0',
            {
              'border-transparent focus:border-soft hover:border-soft': !hasError,
              'border-critical-no-bg focus:border-critical-no-bg': hasError,
              'fill-primary': !readOnly && !disabled && variant === DEBOUNCE_VARIANT_LIGHT,
              'fill-secondary': !readOnly && !disabled && variant === DEBOUNCE_VARIANT_DARK,
              'cursor-not-allowed fill-primary-disabled': readOnly ?? disabled,
            },
            placeholderColor,
            paddingClasses
          )}
          type='text'
          onChange={handleChange}
          onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
        />
        {canDeleteSearch && inputValue && (
          <button type='button' onClick={handleClear} className='absolute inset-y-0 right-0 pr-3 flex items-center'>
            <IconCross className='h-3 w-3 text-secondary' aria-hidden={true} />
          </button>
        )}
      </div>
    );
  }
);
